import type {
	FilterRuleModel,
	MetricModel,
	OptimizationIDModel,
} from '@/shared/generated'

import { useMemo } from 'react'
import { filter, map, pipe, prop, propEq, reduce, sum } from 'lodash/fp'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	MetricGroup,
	MetricGroupValue,
	useRepricingStatisticQuery,
} from '@/entities/optimization-statistic'

import ProgressRow from '@/shared/components/Progress/ProgressRow'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { formatNumber } from '@/shared/tools/locale'

export default function PricingLinesChangeTypeWidget({
	isLoading: isDataLoading,
	queryParams,
}: {
	queryParams: {
		optimizations: OptimizationIDModel[]
		filters: FilterRuleModel[]
	} | null
	isLoading: boolean
}) {
	const { t } = useTranslation()

	const {
		data: repricingStatistic,
		isLoading,
		isError,
		error,
	} = useRepricingStatisticQuery(queryParams!, {
		enabled: !!queryParams && !isDataLoading,
		select: pipe(
			prop('metrics'),
			filter(propEq('group_type', MetricGroup.PricingType)),
			reduce(
				(acc, item: MetricModel) => {
					const groupValue = item.group_value as MetricGroupValue
					if (!acc[groupValue]) {
						acc[groupValue] = {}
					}
					acc[groupValue][item.name] = item.value

					return acc
				},
				{} as Record<MetricGroupValue, Record<string, number>>
			)
		),
	})
	const rows = useMemo(() => {
		const data = repricingStatistic

		return [
			{
				name: t('status_panel_limit_of_price_change'),
				lines: data?.[MetricGroupValue.Markdown].pricing_line_count,
				products: data?.[MetricGroupValue.Markdown].product_count,
			},
			{
				name: t('status_panel_price_change_by_rb'),
				lines: data?.[MetricGroupValue.RuleBased].pricing_line_count,
				products: data?.[MetricGroupValue.RuleBased].product_count,
			},
			{
				name: t('status_panel_price_change_by_ml'),
				lines: data?.[MetricGroupValue.DemandBased].pricing_line_count,
				products: data?.[MetricGroupValue.DemandBased].product_count,
			},
			{
				name: t('status_panel_price_change_by_promo', {
					defaultValue: 'Prices changed by Promo',
				}),
				lines: data?.[MetricGroupValue.Promo].pricing_line_count,
				products: data?.[MetricGroupValue.Promo].product_count,
			},
			{
				name: t('status_panel_price_change_by_manual'),
				lines: data?.[MetricGroupValue.Manual].pricing_line_count,
				products: data?.[MetricGroupValue.Manual].product_count,
			},
		]
	}, [repricingStatistic])
	const total = sum(map(prop('products'), rows))
	if (isLoading || isDataLoading) {
		return <LoadingPlaceholder />
	}
	if (isError) {
		return (
			<Alert variant='destructive'>
				<AlertTriangleIcon />
				<AlertDescription>
					{error.message || t('fatal_error_title')}
				</AlertDescription>
			</Alert>
		)
	}

	return (
		<div className='flex w-full flex-col space-y-2'>
			{rows.map((row) => (
				<ProgressRow
					key={row.name}
					label={row.name}
					relativeValue={(row.products / total) * 100}
					absoluteValue={formatNumber(row.products)}
					extraValue={formatNumber(row.lines)}
				/>
			))}
		</div>
	)
}

function LoadingPlaceholder() {
	return (
		<div className='flex flex-col items-center space-y-2'>
			<div className='flex w-full items-center justify-between'>
				<Skeleton className='h-5 w-20' />
				<Skeleton className='h-5 w-10' />
			</div>
			<div className='flex w-full items-center justify-between'>
				<Skeleton className='h-5 w-24' />
				<Skeleton className='h-5 w-12' />
			</div>
			<div className='flex w-full items-center justify-between'>
				<Skeleton className='h-5 w-28' />
				<Skeleton className='h-5 w-12' />
			</div>
		</div>
	)
}
